import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import DialedDigitExtractionServerForm from 'routes/DialedDigitExtractionServer/components/DialedDigitExtractionServerForm';
import { Ref } from 'routes/DialedDigitExtractionServer/components/DialedDigitExtractionServerForm/DialedDigitExtractionServerForm';
import { RootState } from 'data/root.reducers';
import * as networkActions from 'data/network/network.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as distributorSelectors from 'data/distributor/distributor.selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as tlsProxySelectors from 'data/tlsProxy/tlsProxy.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as networkSelectors from 'data/network/network.selectors';
import * as deliveryFunctionNetworkInterfaceActions from 'data/deliveryFunctionNetworkInterface/deliveryFunctionNetworkInterface.actions';
import * as nodesSelectors from 'data/nodes/nodes.selectors';
import * as dialedDigitExtractionServerActions from 'data/dialedDigitExtractionServer/dialedDigitExtractionServer.actions';
import { newDialedDigitExtractionServer } from 'routes/DialedDigitExtractionServer/DialedDigitExtractionServer.types';
import { adaptToCreate } from 'data/dialedDigitExtractionServer/dialedDigitExtractionServer.adapter';
import * as tlsProxyActions from 'data/tlsProxy/tlsProxy.actions';
import { DialedDigitExtractionServer } from 'data/dialedDigitExtractionServer/dialedDigitExtractionServer.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormTemplateSection } from 'data/template/template.types';
import { dialedDigitExtractionServerTemplate } from '../../templates';
import { useDialedDigitExtractionServerFieldCreateNew } from '../../hooks/useDialedDigitExtractionServerFieldCreateNew';
import { adaptTemplateVisibilityMode, adaptTemplateOptions, adaptNetworkIdField } from '../../utils/dialedDigitExtractionServerTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/dialedDigitExtractionServerDataToSubmit';

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  dialedDigitExtractionServerTemplateDef: adaptTemplateVisibilityMode(dialedDigitExtractionServerTemplate, 'create'),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  distributors: distributorSelectors.getDistributors(state),
  nodes: nodesSelectors.getNodes(state),
  networkInterfaceList: networkSelectors.getNetworks(state),
  dialedDigitExtractionServerTemplate: adaptTemplateVisibilityMode(dialedDigitExtractionServerTemplate, 'create'),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  tlsProxies: tlsProxySelectors.getTlsProxyList(state),
  availableAccessFunction: supportedFeaturesSelectors.getAvailableAccessFunction(state),
  enabledAccessFunction: supportedFeaturesSelectors.getEnabledAccessFunction(state),
});

const mapDispatch = {
  getNetworkInterfaceList: deliveryFunctionNetworkInterfaceActions.getDeliveryFunctionNetworkInterfaceList,
  createDialedDigitExtractionServer: dialedDigitExtractionServerActions.createDialedDigitExtractionServer,
  goToDialedDigitExtractionServerList: dialedDigitExtractionServerActions.goToDialedDigitExtractionServerList,
  fetchTlsProxyListWithView: tlsProxyActions.fetchTlsProxyListWithView,
  getNetworks: networkActions.getNetworks,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>
export type Props = PropsFromRedux & PropsFromRouter;

export const DialedDigitExtractionServerCreate = ({
  history,
  userId,
  deliveryFunctionId,
  networkInterfaceList,
  getNetworks,
  goToDialedDigitExtractionServerList,
  createDialedDigitExtractionServer,
  userName,
  isAuditEnabled,
  dialedDigitExtractionServerTemplateDef,
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<DialedDigitExtractionServer>(cloneDeep(newDialedDigitExtractionServer));
  const [template, setTemplate] = useState(dialedDigitExtractionServerTemplateDef);
  const { t } = useTranslation();

  const [handleFieldCreateNew] = useDialedDigitExtractionServerFieldCreateNew(
    (field) => handleFieldUpdate(field),
  );

  useEffect(() => {
    getNetworks(deliveryFunctionId);
  }, [deliveryFunctionId, getNetworks]);

  useEffect(() => {
    setTemplate(adaptTemplateOptions(template, networkInterfaceList));
  }, [networkInterfaceList]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData);
    const clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate.key = Date.now().toString();
    clonedTemplate.main = adaptNetworkIdField(template.main as FormTemplateSection, newData);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const getCreateDistributorActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const distributorConfigFormData = formRef.current.getValues();
    const payload = {
      data: adaptToCreate(distributorConfigFormData),
      auditDetails: getAuditDetailsToSubmit(distributorConfigFormData, null, isAuditEnabled, userName, userId, 'CREATE'),
    };

    return payload;

  };

  const handleClickSave = () => {
    const payload = getCreateDistributorActionPayload();
    if (payload) {
      createDialedDigitExtractionServer(deliveryFunctionId, payload, history);
    }
  };

  const handleCancel = () => goToDialedDigitExtractionServerList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <DialedDigitExtractionServerForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
          onFieldCreateNew={handleFieldCreateNew}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="DdeServerCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('dialedDigitExtractionServer.create.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(DialedDigitExtractionServerCreate));
